import React from 'react';
import CardTitle from '@/components/uiKit/Cards/CardTitle';
import { BaseCard } from '@/components/uiKit/Cards';
import { Skeleton } from '@/components/ui/skeleton';

interface EventItem {
  title: string;
  category: string;
  eventCount: number;
  unit?: string;
  percentageChange: number;
  isNegative?: boolean;
  bg?: string;
}

interface EventsDashboardProps {
  events?: EventItem[];
  isLoading?: boolean;
}

const defaultEvents: EventItem[] = [
  {
    title: 'Downhill Gear Selection',
    category: 'Transmission',
    eventCount: 110,
    unit: 'Events',
    percentageChange: 167,
    bg: 'bg-primary-200',
  },
  {
    title: 'Wrong Gear',
    category: 'Basic Engine',
    eventCount: 14,
    unit: 'h 32m',
    percentageChange: 123,
    bg: 'bg-primary-200',
  },
  {
    title: 'Hydraulic Brake Usage',
    category: 'Breaking System',
    eventCount: 85,
    unit: 'Events',
    percentageChange: 107,
    bg: 'bg-primary-200',
  },
  {
    title: 'Wheel Digging',
    category: 'Tyres Transmission',
    eventCount: 292,
    unit: 'Events',
    percentageChange: 35,
    bg: 'bg-primary-200',
  },
  {
    title: 'Hot Engine Shut Off',
    category: 'Engine',
    eventCount: 5,
    unit: 'Events',
    percentageChange: 60,
    isNegative: true,
    bg: 'bg-primary-50',
  },
  {
    title: 'Improper Load Balancing',
    category: 'Suspension',
    eventCount: 86,
    unit: 'Events',
    percentageChange: 57,
    isNegative: true,
    bg: 'bg-primary-50',
  },
  {
    title: 'Hoist Lever Position Abuse',
    category: 'Hoist system',
    eventCount: 20,
    unit: 'h 57m',
    percentageChange: 24,
    isNegative: true,
    bg: 'bg-primary-50',
  },
];

export const EventsDashboard: React.FC<EventsDashboardProps> = ({ events = defaultEvents, isLoading = false }) => {
  const formatPercentage = (percentage: number, isNegative?: boolean) => {
    const sign = isNegative ? '-' : '+';
    return `${sign}${percentage}%`;
  };

  const getPercentageColor = (isNegative?: boolean) => {
    return isNegative ? 'bg-[#01152B]' : 'bg-[#CC0000]';
  };

  const getPercentageTextColor = (isNegative?: boolean) => {
    return isNegative ? 'text-white' : 'text-white';
  };

  return (
    <BaseCard
      variant="wrapper"
      classNames={{
        card: 'min-h-0 flex flex-col h-full  ',
        content: 'h-full flex w-full flex-1 bg-gray-200 gap-2 overflow-auto scrollbar-thin p-0',
      }}
      title={
        <div className="py-1">
          <CardTitle text="Operator proficiency" />
        </div>
      }
    >
      <BaseCard
        classNames={{
          card: 'min-h-0 flex flex-col h-full gap-2 w-full rounded-lg',
          content: 'h-full w-full overflow-auto scrollbar-thin',
          title: 'capitalize',
        }}
      >
        {isLoading ? (
          <Skeleton className="flex flex-1 h-full" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-6xl">
            {events.map((event, index) => (
              <div key={index} className={`${event.bg} rounded-lg p-4 relative ${index === 0 ? 'md:col-span-2' : ''}`}>
                {/* Title and Category */}
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">{event.title}</h3>
                  <p className="text-md text-gray-600">{event.category}</p>
                </div>

                {/* Event Count and Percentage */}
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-lg font-bold text-gray-800">
                      {event.eventCount} {event.unit}
                    </div>
                  </div>

                  <div className="flex items-center">
                    <span
                      className={`px-3 py-1 rounded-lg text-sm font-medium ${getPercentageColor(
                        event.isNegative
                      )} ${getPercentageTextColor(event.isNegative)}`}
                    >
                      {formatPercentage(event.percentageChange, event.isNegative)}
                      {!event.isNegative}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </BaseCard>
    </BaseCard>
  );
};
